import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import B2becTranslation from '@/components/B2becTranslation'
import { NotificationManager } from '@/components/Notification'
import { cartService } from '@/services'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectVoucherCode } from '@/store/selectors/cartSelector'
import { getCartItemsAfterRedeemingVoucher, removeVoucherFromCart } from '@/store/slices/cartSlice'
import useAsync from '@/hooks/useAsync'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { ASYNC_STATUS } from '@/libs/constants'
import { MODULE_CART_CHECKOUT_RIGHTS } from '@/libs/constants/modulerights'

import RedeemVoucherInput from './components/RedeemVoucherInput'
import RemoveVoucher from './components/RemoveVoucher'
import styles from './RedeemVoucher.module.scss'

const redeemVoucher = async (voucherCode: string) => cartService.redeemVoucher({ voucherCode })

const renderSuccessNotification = () => {
  NotificationManager.success({
    message: 'notification.success.redeemVoucher',
  })
}

const getErrorMessage = (validationCode) => {
  switch (validationCode) {
    case 'ERR_01': {
      return 'doesNotExist'
    }
    case 'ERR_02': {
      return 'isOutdated'
    }
    case 'ERR_03': {
      return 'isNotValidYet'
    }
    case 'ERR_04': {
      return 'noMatchingProducts'
    }
    default: {
      return 'isInvalid'
    }
  }
}

const renderErrorNotification = (validationCode) => {
  const errorMessage = `notification.error.redeemVoucher.${
    getErrorMessage(
      validationCode,
    )
  }`
  NotificationManager.error({
    message: errorMessage,
  })
}

interface RedeemVoucherProps {
  isDisabled?: boolean
}

const RedeemVoucher = ({ isDisabled = false }: RedeemVoucherProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const voucherCode = useAppSelector(selectVoucherCode)

  const handleVoucherRedemption = useCallback(
    ({ data }) => {
      const { validationCode } = data

      if (validationCode !== '') {
        renderErrorNotification(validationCode)
      } else {
        dispatch(getCartItemsAfterRedeemingVoucher())
        renderSuccessNotification()
      }
    },
    [dispatch],
  )

  const { execute: executeRedeemVoucher, status: redeemVoucherStatus } = useAsync(
    redeemVoucher,
    false,
    handleVoucherRedemption,
  )

  const handleRemoveVoucher = () => {
    dispatch(removeVoucherFromCart())
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.redeemVoucherContainer}>
        <p className={styles.title}>{t('redeemVoucher.title')}</p>
        <RedeemVoucherInput
          handleRedeemVoucher={executeRedeemVoucher}
          isDisabled={isDisabled}
          isRedeeming={redeemVoucherStatus === ASYNC_STATUS.PENDING}
        />
      </div>
      <div className={styles.information}>
        {voucherCode
          ? (
            <>
              <B2becTranslation
                isComponent
                value="redeemVoucher.thisVoucherCodeHasBeenAddedToYourShoppingCart"
                variables={{ voucherNumber: voucherCode }}
                htmlTags={[
                  <span key="voucher-code" className={styles.voucherCode} />,
                ]}
              />
              <PermissionWrapper
                permission={MODULE_CART_CHECKOUT_RIGHTS.REMOVE_VOUCHER_FROM_CART}
              >
                <RemoveVoucher
                  isDisabled={isDisabled}
                  onRemoveVoucher={handleRemoveVoucher}
                />
              </PermissionWrapper>
            </>
          )
          : null}
      </div>
    </div>
  )
}

export default RedeemVoucher
